import { Box, Switch, Typography } from '@mui/material';
import { useRecoilState } from 'recoil';
import { GetSessionError } from '~/backend/connector-api/connector-components';
import LabeledContainer from '~/components/LabeledContainer';
import { Image3D } from '~/domain/patient/image3d';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { autoSegmentationTaskState } from '~/state/atoms/structureSet/autoSegmentationTaskAtom';
import ImageDataPreview from './ImageDataPreview';
import StructureSetDataPreview from './StructureSetDataPreview';

type ImageAndStructureSetDisplayProps = {
    image?: Image3D;
    structureSet?: StructureSetReadModel;
    isLoading?: boolean;
    error: Error | GetSessionError | null;
    optional?: boolean;
};

export default function ImageAndStructureSetDisplay({
    image,
    structureSet,
    isLoading,
    error,
    optional,
}: ImageAndStructureSetDisplayProps) {
    const [autoSegmentationTask, setAutoSegmentationTask] = useRecoilState(
        autoSegmentationTaskState
    );
    const includeStructureSet =
        autoSegmentationTask?.includeStructureSet ?? false;
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAutoSegmentationTask((prev) => ({
            ...prev,
            includeStructureSet: event.target.checked,
        }));
    };

    return (
        <Box display="flex" gap={1}>
            {error ? (
                <Box
                    width="100%"
                    display="flex"
                    alignItems="center"
                    p={2}
                    sx={(theme) => ({
                        borderRadius: `${theme.shape.borderRadius}px`,
                        border: '1px solid red',
                        backgroundColor: 'rgba(255, 0, 0, 0.1)',
                    })}
                >
                    <Typography
                        variant="body2"
                        color="error"
                        data-testid="session-error"
                    >
                        Error when trying to load session data.
                    </Typography>
                </Box>
            ) : (
                <>
                    <LabeledContainer label={'Image'}>
                        <ImageDataPreview image={image} isLoading={isLoading} />
                    </LabeledContainer>

                    <LabeledContainer label={'Structure set'}>
                        {optional && (
                            <Switch
                                checked={includeStructureSet}
                                onChange={handleChange}
                                inputProps={{ 'aria-label': 'controlled' }}
                                sx={{
                                    position: 'absolute',
                                    right: 0,
                                    top: 0,
                                    zIndex: 1000,
                                }}
                            />
                        )}
                        {optional && !includeStructureSet ? (
                            <Box>
                                <Typography
                                    fontSize={12}
                                    width="80%"
                                    position="relative"
                                >
                                    Merge results with structures from{' '}
                                    {structureSet?.structureSetLabel}
                                </Typography>
                            </Box>
                        ) : (
                            <StructureSetDataPreview
                                structureSet={structureSet}
                                isLoading={isLoading}
                            />
                        )}
                    </LabeledContainer>
                </>
            )}
        </Box>
    );
}
